<template>
<div v-if="verified" class="offer-overlay" @click.self="handleClose">
    <div class="offer-card" :class="{ 'offer-card--closing': closing }">
        <button type="button" class="offer-close" @click="handleClose">&times;</button>

        <!-- STEP 1: Offer details -->
        <template v-if="modalStep === 'offer'">
            <div class="offer-header">
                <h5 class="offer-title">Exclusive Offer From PFM Verify</h5>
                <h6 class="offer-subtitle">Sign up today and unlock a $100 Rewards Bonus</h6>
            </div>

            <div class="offer-body">
                <p>
                    You've unlocked early access to a powerful personal financial tool designed to help you stay one step ahead—PFM Verify.
                    <strong>Sign up today and get a bonus $100 in rewards</strong> to spend in our perks and rewards platform—saving you even more at the places you already shop.
                </p>
                <p>
                    With your matched providers reviewing your credit profile and financial history, now's the perfect time to take control and see exactly what they see.
                </p>

                <p class="features-intro">PFM Verify gives you:</p>
                <ul class="features-list">
                    <li><strong>$25/month in rewards</strong> through our perks and rewards platform—redeem at popular retailers, restaurants, and travel brands</li>
                    <li>24/7 access to your latest credit score and full credit report</li>
                    <li>Personalized budgeting tools to track spending and build smarter habits</li>
                    <li>Real-time alerts to help you catch fraud and monitor changes in your report</li>
                    <li>A full {{ offerData.term }}-month window of insight to help you stay financially ready</li>
                </ul>

                <p>
                    Enrollment is simple and fully optional—many users choose to activate their account for less than {{ offerData.priceMonthly }}/month, billed once for the full term.
                </p>

                <div class="account-hint">
                    You can get started now using your {{ offerData.institutionName || 'bank' }} account ending in <strong>{{ offerData.maskedAccountNumber || '****' }}</strong>.
                </div>

                <p class="bonus-note">
                    This special bonus is available with your current application—claim it today and start saving immediately.
                </p>
            </div>

            <div class="offer-actions">
                <button type="button" class="offer-btn offer-btn-cta" @click="modalStep = 'consent'">
                    Activate My Reward + Credit Tools
                </button>
                <button type="button" class="offer-btn offer-btn-skip" @click="handleClose">
                    Not Interested
                </button>
            </div>

            <div class="offer-footer">
                <p>Take a few seconds now to get the complete picture. Take control of your finances today—and improve your chances of getting matched tomorrow.</p>
                <p class="offer-disclaimer">
                    This is a sponsored offer and has no impact on your loan application. By enrolling, you agree to
                    <a href="https://www.pfmverify.ca/terms" target="_blank" rel="nofollow external noopener noreferrer">PFM Verify's Terms of Use</a>.
                    Your one-time payment of {{ offerData.priceTotal }} for {{ offerData.term }} months of access will be processed within 3–5 business days.
                </p>
            </div>
        </template>

        <!-- STEP 2: Consent / PAD Agreement -->
        <template v-if="modalStep === 'consent'">
            <div class="offer-header">
                <h5 class="offer-title">Pre-Authorized Debit (PAD) Agreement</h5>
            </div>

            <div v-if="submitting" class="consent-loading">
                <div class="spinner"></div>
            </div>

            <div v-else-if="submissionMessage" class="consent-message">
                <p>{{ submissionMessage }}</p>
            </div>

            <div v-else class="offer-body">
                <div class="terms-box">
                    <p>
                        By checking the box below, you authorize <strong>{{ crediqueLegalName }} (operating PFM Verify)</strong> to debit your bank account for a one-time payment of
                        <strong>{{ offerData.priceTotal }}</strong>, which covers {{ offerData.term }} months of access to the PFM Verify platform.
                        This amount is equivalent to {{ offerData.priceMonthly }} per month, billed in full today.
                    </p>
                    <p>This debit will be processed via Electronic Funds Transfer (EFT) using the banking information you have provided through Flinks.</p>

                    <p><strong>Details of this Agreement:</strong></p>
                    <ul>
                        <li><strong>Amount:</strong> {{ offerData.priceTotal }} (one-time charge)</li>
                        <li><strong>Frequency:</strong> One-time debit</li>
                        <li><strong>Payee:</strong> {{ crediqueLegalName }} (operating PFM Verify)</li>
                        <li><strong>Charge Date:</strong> Immediately upon authorization</li>
                        <li><strong>Purpose:</strong> Subscription to PFM Verify</li>
                        <li><strong>Cancellation Rights:</strong> You may revoke this authorization any time before the debit is processed by contacting us at
                            <a href="mailto:support@pfmverify.ca">support@pfmverify.ca</a> or by phone at
                            <a href="tel:18885551234">1-888-555-1234</a>.
                        </li>
                    </ul>
                    <p>
                        Accept Pay Global (Payee) has been contracted by {{ crediqueLegalName }} (Entity providing the Payor Goods and Services). You the Payor, authorize Accept Pay Global (Payee) to debit the bank account identified on this form.
                    </p>
                </div>

                <p class="recourse-note">
                    You have certain recourse rights if any debit does not comply with this agreement. For example, you have the right to receive reimbursement for any debit that is not authorized or is not consistent with this PAD Agreement. To obtain more information on your recourse rights, you may contact your financial institution or visit www.payments.ca.
                </p>

                <div class="consent-box">
                    <label class="consent-label">
                        <input v-model="consentChecked" type="checkbox" class="consent-checkbox">
                        I authorize <strong>{{ crediqueLegalName }}</strong> to debit my bank account in the amount of {{ offerData.priceTotal }} as described above. I understand this is a one-time
                        payment for {{ offerData.term }} months of access to PFM Verify. <strong>I waive the 10-day pre-notification</strong> requirement and will receive a confirmation link after the payment is processed.
                    </label>

                    <div class="consent-more">
                        <button type="button" class="consent-toggle" @click="showMore = !showMore">
                            {{ showMore ? 'Less &#9652;' : 'More &#9662;' }}
                        </button>
                        <div v-if="showMore" class="consent-extra">
                            <p>
                                My authorization will be securely stored by {{ crediqueLegalName }} for at least 12 months in compliance with Payments Canada Rule H1.
                                No additional debits will be made without my explicit consent.
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        class="offer-btn offer-btn-cta consent-enroll"
                        :disabled="!consentChecked || submitting"
                        @click="enroll"
                    >
                        Enroll
                    </button>
                </div>

                <button type="button" class="back-link" @click="modalStep = 'offer'">
                    Back to offer details
                </button>
            </div>
        </template>
    </div>
</div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { postAndReturnData } from '@/utils/api.js';

const props = defineProps({
    app: {
        type: Object,
        required: true,
    },
    product: {
        type: String,
        default: null,
    },
    step: {
        type: Number,
        default: null,
    },
});

const emit = defineEmits(['close']);

function logOfferEvent(event) {
    postAndReturnData('/portal/credique/offer/log', {
        event,
        unique_id: props.app.unique_id,
        product: props.product,
        step: props.step,
    });
}

function handleClose() {
    if (closing.value) {
        return;
    }
    // logOfferEvent('closed');
    closing.value = true;
    setTimeout(() => emit('close'), 500);
}

const urlParams = new URLSearchParams(window.location.search);
const isTest = urlParams.has('test_modal_offers') || urlParams.has('test_credique_offer');

const crediqueLegalName = 'Credique Canada Inc.';

const closing = ref(false);
const verified = ref(false);
const modalStep = ref('offer');
const consentChecked = ref(false);
const showMore = ref(false);
const submitting = ref(false);
const submissionMessage = ref(null);
const autoCloseDelayMs = 3500;

const offerData = ref({
    term: '',
    priceTotal: '',
    priceMonthly: '',
    institutionName: '',
    maskedAccountNumber: '',
});

const qualifiedData = ref(null);

onMounted(async () => {
    try {
        const response = await postAndReturnData('/portal/credique/offer/check', {
            unique_id: props.app.unique_id,
            is_test: isTest,
            source: 'application_form',
        });

        if (response && response.offer_details) {
            qualifiedData.value = response;
            offerData.value = {
                term: response.offer_details.term,
                priceTotal: response.offer_details.price_total,
                priceMonthly: response.offer_details.price_monthly,
                institutionName: response.institution_name || '',
                maskedAccountNumber: response.masked_account_number || '',
            };
            verified.value = true;
            logOfferEvent('shown');
        } else {
            emit('close');
        }
    } catch {
        emit('close');
    }
});

async function enroll() {
    if (!consentChecked.value || submitting.value) {
        return;
    }
    submitting.value = true;
    try {
        const response = await postAndReturnData('/portal/credique/offer/enroll', {
            lead_unique_id: qualifiedData.value.lead_unique_id || (isTest && urlParams.get('uid')) || qualifiedData.value.lead_unique_id,
            flinks_log_id: qualifiedData.value.flinks_log_id,
            is_test: isTest || qualifiedData.value.is_test || false,
            product_id: qualifiedData.value.offer_details?.product_id,
            ab_testing_version: qualifiedData.value.offer_details?.ab_testing_version,
            source: 'application_form',
        });

        if (response && response.status === 'success') {
            logOfferEvent('enrolled');
            submissionMessage.value = "You're all set! Check your email for next steps. You can now continue with your application.";
            setTimeout(() => handleClose(), autoCloseDelayMs);
        } else {
            submissionMessage.value = 'We were unable to complete your request at this time. Please try again later.';
        }
    } catch {
        submissionMessage.value = 'We were unable to complete your request at this time. Please try again later.';
    } finally {
        submitting.value = false;
    }
}
</script>

<style scoped>
.offer-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.55);
    z-index: 9999;
    padding: 1rem;
}

.offer-card {
    position: relative;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    max-width: 620px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    padding: 2rem 2rem 1rem 2rem;
    animation: offerBounceIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes offerBounceIn {
    0% { opacity: 0; transform: scale(0.7); }
    60% { opacity: 1; transform: scale(1.08); }
    80% { transform: scale(0.96); }
    100% { opacity: 1; transform: scale(1); }
}

.offer-card--closing {
    animation: offerBounceOut 0.5s cubic-bezier(0.36, 0, 0.66, -0.56) forwards;
}

@keyframes offerBounceOut {
    0% { opacity: 1; transform: scale(1); }
    20% { transform: scale(1.05); }
    100% { opacity: 0; transform: scale(0.7); }
}

.offer-close {
    position: absolute;
    top: 12px;
    right: 16px;
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #999;
    cursor: pointer;
    line-height: 1;
    padding: 0;
}
.offer-close:hover { color: #333; }

/* Header */
.offer-header {
    text-align: center;
    margin-bottom: 1rem;
}
.offer-title {
    color: #0b55c3;
    font-weight: 700;
    font-size: 1.25rem;
    margin: 0 0 0.25rem;
}
.offer-subtitle {
    font-weight: 700;
    font-size: 1rem;
    margin: 0;
}

/* Body */
.offer-body {
    font-size: 0.9rem;
    line-height: 1.6;
    color: #333;
}
.offer-body p { margin: 0 0 0.75rem; }

.features-intro { margin-bottom: 0.25rem !important; }
.features-list {
    padding-left: 1.25rem;
    margin: 0 0 0.75rem;
}
.features-list li { margin-bottom: 0.35rem; }

.account-hint {
    background: #f1f1f1;
    padding: 0.75rem 1rem;
    border-radius: 5px;
    margin-bottom: 0.75rem;
}

.bonus-note {
    text-align: center;
    font-size: 0.85rem;
    color: #555;
}

/* Actions */
.offer-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    margin: 1rem 0;
}

.offer-btn {
    display: block;
    width: 70%;
    padding: 12px 20px;
    font-size: 1rem;
    font-weight: 700;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    text-align: center;
    transition: background-color 0.15s ease, transform 0.1s ease;
}
.offer-btn:active { transform: scale(0.98); }

.offer-btn-cta {
    background: #fd7e14;
    color: #fff;
    border-color: #fd7e14;
}
.offer-btn-cta:hover { background: #e8700e; }
.offer-btn-cta:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.offer-btn-skip {
    background: #f1f1f1;
    color: #333;
    border: 1px solid #cecece;
    font-weight: 400;
}
.offer-btn-skip:hover { background: #e5e5e5; }

/* Footer */
.offer-footer {
    font-size: 0.8rem;
    color: #666;
    line-height: 1.5;
}
.offer-footer p { margin: 0 0 0.5rem; }
.offer-disclaimer { font-size: 0.78rem; color: #888; }
.offer-disclaimer a { color: #0b55c3; }

/* Consent step */
.terms-box {
    border: 1px solid #ccc;
    padding: 1rem;
    max-height: 150px;
    overflow-y: auto;
    margin-bottom: 1rem;
    background: #fafafa;
    font-size: 0.85rem;
    line-height: 1.5;
    border-radius: 5px;
}
.terms-box p { margin: 0 0 0.5rem; }
.terms-box ul {
    padding-left: 1.25rem;
    margin: 0 0 0.5rem;
}

.recourse-note {
    font-size: 0.85rem;
    color: #555;
}

.consent-box {
    background: #f1f1f1;
    border: 1px solid #ccc;
    border-radius: 5px;
    padding: 1rem;
    margin: 1rem 0;
}

.consent-label {
    font-size: 0.85rem;
    line-height: 1.5;
    cursor: pointer;
    display: block;
}

.consent-checkbox {
    margin-right: 0.4rem;
    vertical-align: middle;
}

.consent-toggle {
    background: none;
    border: none;
    color: #0b3d91;
    cursor: pointer;
    font-weight: 700;
    font-size: 0.85rem;
    padding: 0;
    margin-top: 0.75rem;
}

.consent-extra {
    margin-top: 0.5rem;
    font-size: 0.85rem;
    color: #555;
}
.consent-extra p { margin: 0; }

.consent-enroll {
    width: 60%;
    margin: 1rem auto 0;
    display: block;
}

.back-link {
    background: none;
    border: none;
    color: #999;
    cursor: pointer;
    font-size: 0.85rem;
    padding: 0;
    margin-top: 0.5rem;
}
.back-link:hover { color: #333; }

/* Loading / message */
.consent-loading {
    display: flex;
    justify-content: center;
    padding: 3rem 0;
}
.spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #e5e5e5;
    border-top-color: #0b55c3;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}
@keyframes spin {
    to { transform: rotate(360deg); }
}

.consent-message {
    text-align: center;
    padding: 2rem 1rem;
    font-size: 1rem;
    color: #333;
}
</style>
